import { Injectable, signal, WritableSignal } from '@angular/core';

/**
 * A service responsible for managing and updating a query signal.
 * Provides functionality to add a new query or clear an existing query.
 *
 * The `query` is a readonly signal that represents the current state
 * of the query. The state can be updated via the `addQuery` method and reset
 * using the `clearQuery` method.
 *
 * Utilizes Angular's dependency injection and is provided at the root level.
 */
@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly querySignal: WritableSignal<string | null> = signal(null);

  /**
   * A readonly signal that holds the current search query value.
   * Components can subscribe to this signal for reactive updates.
   */
  public readonly query = this.querySignal.asReadonly();

  /**
   * Sets a new query string in the signal state.
   * @param query - The new query string to store.
   */
  public addQuery(query: string): void {
    this.querySignal.set(query);
  }

  /**
   * Clears the current query by resetting the signal to null.
   */
  public clearQuery(): void {
    this.querySignal.set(null);
  }
}
